import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from 'src/utils/sentry';

import ErrorPage from '../pages/Error';

interface Props {
  fallback?: ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      errorMessage: error.message || error.toString(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Example "componentStack":
    //   in ComponentThatThrows (created by App)
    //   in ErrorBoundary (created by App)
    //   in div (created by App)
    //   in App
    //logErrorToMyService(error, errorInfo.componentStack);

    // Capture error in Sentry with React component stack
    captureException(error, errorInfo);

    // eslint-disable-next-line no-console
    console.error(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorPage message={this.state.errorMessage} />;
    }
    return this.props.children;
  }
}
